import { isSignInWithEmailLink } from 'firebase/auth';
import { firebase } from './firebase.js';
import { isNative } from './device.js';

/**
 * Universal Links (iOS) / App Links (Android).
 *
 * `posture-fe600.web.app` and `.firebaseapp.com` serve
 * `/.well-known/apple-app-site-association` and `assetlinks.json`, so tapping
 * an email sign-in link opens the app instead of the browser. The app gets the
 * full https URL here, at launch or while running.
 *
 * Only the email sign-in link is claimed on iOS (AASA components); Android
 * claims `/login` and `/__/auth/action` (intent filters cannot match a query).
 */
export interface AppLinkHandlers {
  /** An email sign-in link. Resolve true once signed in. */
  onEmailSignIn(link: string): Promise<boolean>;
  /** Any other claimed URL: an in-app path to show. */
  onPath(path: string): void;
}

export async function listenForAppLinks(handlers: AppLinkHandlers): Promise<void> {
  if (!isNative()) return;
  const { App } = await import('@capacitor/app');

  const handle = (url: string): void => {
    if (isSignInWithEmailLink(firebase().auth, url)) {
      void handlers.onEmailSignIn(url).then((ok) => handlers.onPath(ok ? '/progress' : '/login'));
      return;
    }
    try {
      const { pathname } = new URL(url);
      handlers.onPath(pathname.startsWith('/__/') ? '/login' : pathname);
    } catch {
      // Not a URL we can route; stay where we are.
    }
  };

  await App.addListener('appUrlOpen', ({ url }) => handle(url));
  // Cold start: the link that launched the app arrived before the listener.
  const launch = await App.getLaunchUrl();
  if (launch?.url) handle(launch.url);
}
